/**
 * Staff "My Schedule" Page
 * Mobile-friendly page for staff to view their assigned shifts
 * Supports offline shift acknowledgment with automatic sync
 */

import React, { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Calendar,
  Clock,
  MapPin,
  ChevronLeft,
  ChevronRight,
  User,
  FileText,
  Play,
  Loader2,
  CalendarDays,
  AlertCircle,
  CheckCircle,
  Cloud,
  CloudOff
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, addWeeks, addDays, isToday, isSameDay, parseISO } from 'date-fns';
import { useScheduleAckAction } from '@/hooks/useOfflineAction';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import MyClockCard, { MY_CLOCK_ACTION } from '@/components/timeclock/MyClockCard';

const API = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_STYLES = {
  PUBLISHED: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Scheduled' },
  DRAFT: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pending' },
  CANCELED: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Cancelled' },
};

// Acknowledgment status styles
const ACK_STYLES = {
  ACKNOWLEDGED: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Confirmed', icon: CheckCircle },
  DECLINED: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Declined', icon: AlertCircle },
  CHANGE_REQUESTED: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Change Requested', icon: Clock },
  PENDING_SYNC: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Syncing...', icon: CloudOff },
};

// Format time (e.g., "9:00 AM")
const formatTime = (isoString) => {
  if (!isoString) return '';
  return format(parseISO(isoString), 'h:mm a');
};

// Format date (e.g., "Mon, Mar 6")
const formatShortDate = (isoString) => {
  if (!isoString) return '';
  return format(parseISO(isoString), 'EEE, MMM d');
};

// Group shifts by date
const groupShiftsByDate = (shifts) => {
  const grouped = {};
  shifts.forEach(item => {
    const dateKey = format(parseISO(item.shift.start_at), 'yyyy-MM-dd');
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(item);
  });
  return grouped;
};

export default function MySchedulePage() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [clockingIn, setClockingIn] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [pendingAcks, setPendingAcks] = useState({}); // Track pending acks by shift ID
  
  // Date range state
  const [currentWeekStart, setCurrentWeekStart] = useState(() => 
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [viewMode, setViewMode] = useState('week'); // week, next, month

  // Offline sync hooks
  const { execute: executeAck, isExecuting: isAckExecuting } = useScheduleAckAction();
  const { isOnline } = useOfflineSync();

  // Get token from localStorage
  const getToken = () => {
    // the staff session: the staff PIN sign-in (/staff/login or the POS; pos_token:
    // typ "staff" with the staff member's restaurant and role). A customer
    // sign-in names no restaurant and is never a staff session.
    return localStorage.getItem('pos_token');
  };

  // Get user ID for deduplication
  const getUserId = () => {
    try {
      const token = getToken();
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub || payload.user_id || payload.email;
      }
    } catch (e) {}
    return 'unknown';
  };

  // Handle shift acknowledgment
  const handleAcknowledgeShift = async (shift, action = 'acknowledge') => {
    setAcknowledging(true);
    const shiftId = shift.id;
    
    // Mark as pending sync
    setPendingAcks(prev => ({ ...prev, [shiftId]: true }));
    
    try {
      const result = await executeAck({
        payload: {
          shift_id: shiftId,
          action: action
        },
        entityId: shiftId,
        userId: getUserId()
      });

      if (result.queued) {
        toast.success('Acknowledgment saved offline. Will sync when online.', {
          icon: <CloudOff className="w-4 h-4" />
        });
      } else if (result.ok) {
        setPendingAcks(prev => ({ ...prev, [shiftId]: false }));
        toast.success(`Shift ${action === 'acknowledge' ? 'confirmed' : action}!`, {
          icon: <CheckCircle className="w-4 h-4" />
        });
        // Refresh to get authoritative state
        fetchShifts();
      } else {
        throw new Error(result.error || 'Failed to acknowledge shift');
      }
    } catch (err) {
      console.error('Error acknowledging shift:', err);
      setPendingAcks(prev => ({ ...prev, [shiftId]: false }));
      toast.error('Failed to acknowledge shift. Please try again.');
    } finally {
      setAcknowledging(false);
    }
  };

  // Calculate date range
  const getDateRange = () => {
    let from, to;
    const now = new Date();
    
    switch (viewMode) {
      case 'week':
        from = currentWeekStart;
        to = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
        break;
      case 'next':
        from = addWeeks(startOfWeek(now, { weekStartsOn: 1 }), 1);
        to = endOfWeek(from, { weekStartsOn: 1 });
        break;
      case 'month':
        from = now;
        to = addDays(now, 30);
        break;
      default:
        from = currentWeekStart;
        to = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
    }
    
    return { from, to };
  };

  // Fetch shifts
  const fetchShifts = useLatestCallback(async () => {
    setLoading(true);
    setError(null);
    
    const token = getToken();
    if (!token) {
      setError('Please log in to view your schedule');
      setLoading(false);
      return;
    }

    const { from, to } = getDateRange();
    
    try {
      const response = await fetch(
        `${API}/api/labor/shifts/me?from=${format(from, 'yyyy-MM-dd')}&to=${format(to, 'yyyy-MM-dd')}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          setError('Session expired. Please log in again.');
          return;
        }
        throw new Error('Failed to fetch schedule');
      }

      const data = await response.json();
      setShifts(data.shifts || []);
    } catch (err) {
      console.error('Error fetching shifts:', err);
      setError('Unable to load schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  });

  // Handle clock in
  // The staff member's own time clock (components/timeclock/MyClockCard: labor-service
  // /api/timeclock/v2 -> time_clock_entries, the rows Payroll pays). It asks for the job
  // when there are several and keeps the punch on the phone when offline.
  const handleClockIn = () => {
    if (!getToken()) {
      toast.error('Please log in to clock in');
      return;
    }
    setSheetOpen(false);
    window.dispatchEvent(new CustomEvent(MY_CLOCK_ACTION, { detail: { action: 'CLOCK_IN' } }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Check if shift is "active" (can clock in)
  const isShiftActive = (shift) => {
    const now = new Date();
    const start = parseISO(shift.start_at);
    const end = parseISO(shift.end_at);
    const earlyBuffer = 15 * 60 * 1000; // 15 minutes
    
    return now >= new Date(start.getTime() - earlyBuffer) && now <= end;
  };

  // Navigate weeks
  const goToPreviousWeek = () => {
    setCurrentWeekStart(prev => addWeeks(prev, -1));
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(prev => addWeeks(prev, 1));
  };

  const goToThisWeek = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  };

  // Fetch on mount and when date range changes
  useEffect(() => {
    fetchShifts();
  }, [currentWeekStart, viewMode, fetchShifts]);

  // Group shifts by date
  const groupedShifts = groupShiftsByDate(shifts);
  const sortedDates = Object.keys(groupedShifts).sort();

  // Calculate total hours for the period
  const totalHours = shifts.reduce((sum, item) => sum + (item.shift.duration_hours || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/staff/login" className="p-2 hover:bg-white/10 rounded-lg" aria-label="Staff sign-in">
                <ChevronLeft className="w-5 h-5 text-white" />
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-white">My Schedule</h1>
                <p className="text-xs text-slate-400">
                  {format(currentWeekStart, 'MMM d')} - {format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'MMM d, yyyy')}
                </p>
              </div>
            </div>
            
            {/* "/clock" was no page on this host (it opened the storefront) */}
            <Link to="/staff/availability">
              <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                <Clock className="w-4 h-4 mr-1" />
                Availability
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* View Controls */}
      <div className="border-b border-white/10 bg-black/20">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            {/* Week Navigation */}
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={goToPreviousWeek}
                className="text-white hover:bg-white/10 p-2"
                data-testid="prev-week-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={goToThisWeek}
                className="text-white hover:bg-white/10 text-xs px-2"
                data-testid="today-btn"
              >
                Today
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={goToNextWeek}
                className="text-white hover:bg-white/10 p-2"
                data-testid="next-week-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* View Mode */}
            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger className="w-28 h-8 bg-white/5 border-white/10 text-white text-xs" data-testid="view-mode-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week" data-testid="view-this-week">This Week</SelectItem>
                <SelectItem value="next" data-testid="view-next-week">Next Week</SelectItem>
                <SelectItem value="month" data-testid="view-30-days">30 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="container mx-auto px-4 py-4">
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/20">
                  <CalendarDays className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Scheduled</p>
                  <p className="text-xl font-bold text-white">{shifts.length} shifts</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-400">Total Hours</p>
                <p className="text-xl font-bold text-orange-400">{totalHours.toFixed(1)}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My time clock */}
      <div className="container mx-auto px-4 pb-4">
        <MyClockCard />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 pb-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-orange-400 animate-spin mb-3" />
            <p className="text-slate-400">Loading schedule...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
            <p className="text-white mb-2">{error}</p>
            <Button onClick={fetchShifts} variant="outline" className="border-white/20 text-white">
              Try Again
            </Button>
          </div>
        ) : shifts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Calendar className="w-12 h-12 text-slate-600 mb-3" />
            <p className="text-white text-lg mb-1">No shifts scheduled</p>
            <p className="text-slate-400 text-sm">You have no shifts for this period</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedDates.map(dateKey => {
              const dayShifts = groupedShifts[dateKey];
              const dateObj = parseISO(dateKey);
              const isCurrentDay = isToday(dateObj);
              
              return (
                <div key={dateKey}>
                  {/* Date Header */}
                  <div className={`flex items-center gap-2 mb-2 ${isCurrentDay ? 'text-orange-400' : 'text-slate-400'}`}>
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {isCurrentDay ? 'Today' : format(dateObj, 'EEEE')} — {format(dateObj, 'MMMM d')}
                    </span>
                    {isCurrentDay && (
                      <Badge className="bg-orange-500/20 text-orange-400 text-xs">Today</Badge>
                    )}
                  </div>
                  
                  {/* Shifts for this day */}
                  <div className="space-y-2">
                    {dayShifts.map(item => {
                      const { shift, assignment } = item;
                      const status = STATUS_STYLES[shift.status] || STATUS_STYLES.PUBLISHED;
                      const canClockIn = isShiftActive(shift) && shift.status === 'PUBLISHED';
                      
                      return (
                        <Card 
                          key={shift.id}
                          className={`bg-white/5 border-white/10 cursor-pointer hover:bg-white/10 transition-colors ${
                            isCurrentDay ? 'border-l-2 border-l-orange-500' : ''
                          }`}
                          onClick={() => {
                            setSelectedShift(item);
                            setSheetOpen(true);
                          }}
                          data-testid={`shift-card-${shift.id}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="text-white font-medium">{shift.title || 'Shift'}</h3>
                                  <Badge className={`${status.bg} ${status.text} text-xs`}>
                                    {status.label}
                                  </Badge>
                                </div>
                                
                                <div className="flex items-center gap-4 text-sm text-slate-400">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3.5 h-3.5" />
                                    {formatTime(shift.start_at)} - {formatTime(shift.end_at)}
                                  </span>
                                  {shift.station && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-3.5 h-3.5" />
                                      {shift.station}
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {canClockIn && (
                                <Button
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClockIn(shift);
                                  }}
                                  disabled={clockingIn}
                                >
                                  {clockingIn ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Play className="w-4 h-4 mr-1" />
                                      Clock In
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Shift Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="bg-slate-900 border-white/10 rounded-t-2xl">
          {selectedShift && (
            <>
              <SheetHeader className="pb-4">
                <SheetTitle className="text-white text-left">
                  {selectedShift.shift.title || 'Shift Details'}
                </SheetTitle>
              </SheetHeader>
              
              <div className="space-y-4 pb-6">
                {/* Time */}
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/10">
                    <Clock className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Time</p>
                    <p className="text-white">
                      {formatTime(selectedShift.shift.start_at)} - {formatTime(selectedShift.shift.end_at)}
                    </p>
                  </div>
                </div>
                
                {/* Date */}
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/10">
                    <Calendar className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Date</p>
                    <p className="text-white">{formatShortDate(selectedShift.shift.start_at)}</p>
                  </div>
                </div>
                
                {/* Station */}
                {selectedShift.shift.station && (
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white/10">
                      <MapPin className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Station</p>
                      <p className="text-white">{selectedShift.shift.station}</p>
                    </div>
                  </div>
                )}

                {/* Role */}
                {selectedShift.shift.role_name && (
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white/10">
                      <User className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Role</p>
                      <p className="text-white">{selectedShift.shift.role_name}</p>
                    </div>
                  </div>
                )}
                
                {/* Notes */}
                {selectedShift.shift.notes && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-white/10">
                      <FileText className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Notes</p>
                      <p className="text-white">{selectedShift.shift.notes}</p>
                    </div>
                  </div>
                )}

                {/* Acknowledgment Status */}
                {selectedShift.shift.ack_status && (
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white/10">
                      <CheckCircle className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Acknowledgment</p>
                      <Badge className={`${ACK_STYLES[selectedShift.shift.ack_status]?.bg} ${ACK_STYLES[selectedShift.shift.ack_status]?.text}`}>
                        {ACK_STYLES[selectedShift.shift.ack_status]?.label || selectedShift.shift.ack_status}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Acknowledge Button - Show only for published shifts without acknowledgment */}
                {selectedShift.shift.status === 'PUBLISHED' && !selectedShift.shift.ack_status && (
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white mt-4"
                    onClick={() => handleAcknowledgeShift(selectedShift.shift)}
                    disabled={acknowledging || pendingAcks[selectedShift.shift.id]}
                    data-testid="acknowledge-shift-btn"
                  >
                    {(acknowledging || pendingAcks[selectedShift.shift.id]) ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    {pendingAcks[selectedShift.shift.id] ? 'Syncing...' : 'Confirm Shift'}
                  </Button>
                )}

                {/* Pending Sync Badge */}
                {pendingAcks[selectedShift.shift.id] && (
                  <Badge className="w-full justify-center bg-blue-500/20 text-blue-400 mt-2">
                    <CloudOff className="w-3 h-3 mr-1" />
                    Pending sync
                  </Badge>
                )}

                {/* Clock In Button */}
                {isShiftActive(selectedShift.shift) && selectedShift.shift.status === 'PUBLISHED' && (
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white mt-4"
                    onClick={() => handleClockIn(selectedShift.shift)}
                    disabled={clockingIn}
                  >
                    {clockingIn ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Clock In Now
                  </Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
